import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

export const couponKeys = {
  all: ['coupons'],
  lists: () => [...couponKeys.all, 'list'],
  list: (filters) => [...couponKeys.lists(), filters],
  deferred: (filters) => [...couponKeys.all, 'deferred', filters],
  details: () => [...couponKeys.all, 'detail'],
  detail: (id) => [...couponKeys.details(), id],
  generationControl: (filters) => [...couponKeys.all, 'generation-control', filters],
  checkQuota: () => [...couponKeys.all, 'check-quota'],
  pdf: (id) => [...couponKeys.all, 'pdf', id],
  externalPayments: () => [...couponKeys.all, 'external-payments'],
  paidsDetails: () => [...couponKeys.all, 'paids-details'],
  contractFilter: (filters) => [...couponKeys.all, 'contract-filter', filters],
  naranjaXPayments: (date) => [...couponKeys.all, 'naranjax-payments', date],
};

export const useCoupons = (filters = {}) => {
  return useQuery({
    queryKey: couponKeys.list(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };
      if (cleanFilters.quarter === '') delete cleanFilters.quarter;
      if (cleanFilters.group === '') delete cleanFilters.group;

      const response = await apiClient.get('coupons/filter', {
        params: cleanFilters,
      });
      return response.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCouponsDeferred = (filters = {}) => {
  return useQuery({
    queryKey: couponKeys.deferred(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };
      if (cleanFilters.quarter === '') delete cleanFilters.quarter;
      if (cleanFilters.group === '') delete cleanFilters.group;

      const response = await apiClient.get('coupons/filter/deferred', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCoupon = (id, options = {}) => {
  return useQuery({
    queryKey: couponKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`coupons/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const useGenerationControl = (filters = {}) => {
  const defaultFilters = {
    branch: '',
    plan: '',
    group: '',
    payment_method: '',
    year: null,
    quarter: null,
    contract_number: '',
  };

  const mergedFilters = { ...defaultFilters, ...filters };

  return useQuery({
    queryKey: couponKeys.generationControl(mergedFilters),
    queryFn: async () => {
      const response = await apiClient.get('coupons/generation-control', {
        params: mergedFilters,
      });
      return response.data;
    },
    staleTime: 3 * 60 * 1000,
  });
};

export const useContractFilter = () => {
  return useMutation({
    mutationFn: async (filters) => {
      const response = await apiClient.post('coupons/filter/payment', filters);
      return response.data;
    },
    onError: (error) => {
      console.error('Error filtering contracts:', error);
    },
  });
};

export const useNaranjaXPayments = (date, options = {}) => {
  return useQuery({
    queryKey: couponKeys.naranjaXPayments(date),
    queryFn: async () => {
      const response = await apiClient.get(`coupons/payments/naranjax?date=${date}`);
      return response.data;
    },
    enabled: !!date,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useNaranjaXPaymentsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (date) => {
      const response = await apiClient.get(`coupons/payments/naranjax?date=${date}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.naranjaXPayments() });
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
    },
    onError: (error) => {
      console.error('Error consolidating Naranja X payments:', error);
    },
  });
};

export const useCouponPdf = (id, options = {}) => {
  return useQuery({
    queryKey: couponKeys.pdf(id),
    queryFn: async () => {
      const response = await apiClient.get(`coupons/print/${id}`);
      return response.data;
    },
    enabled: false,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
};

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`coupons/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
      queryClient.removeQueries({ queryKey: couponKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting coupon:', error);
    },
  });
};

export const useRegenerateCouponQrs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.post(`coupons/${id}/regenerate-qrs`);
      return response.data;
    },
    onSuccess: (data, regeneratedId) => {
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
      if (regeneratedId) {
        queryClient.invalidateQueries({ queryKey: couponKeys.detail(regeneratedId) });
      }
    },
    onError: (error) => {
      console.error('Error regenerating coupon QRs:', error);
    },
  });
};

export const useAddCouponsByContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (coupon) => {
      const response = await apiClient.post(`coupons/${coupon.numberContract}`, coupon);
      return { data: response.data, status: response.status };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
    },
    onError: (error) => {
      console.error('Error adding coupons by contract:', error);
    },
  });
};

export const useAddCoupons = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (coupon) => {
      const cleanedData = {
        ...coupon,
        contract_number: coupon.numberContract || coupon.contract_number || '',
      };
      delete cleanedData.numberContract;

      const response = await apiClient.post('coupons', cleanedData);
      return { data: response.data, status: response.status };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
    },
    onError: (error) => {
      console.error('Error adding coupons:', error);
    },
  });
};

export const useUpdateCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`coupons/${id}`, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: couponKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
    },
    onError: (error) => {
      console.error('Error updating coupon:', error);
    },
  });
};

export const useCheckQuota = () => {
  return useMutation({
    mutationFn: async (requestData) => {
      const cleanedData = {
        uuid_payment_method: requestData.uuid_payment_method,
        uuid_group: requestData.uuid_group,
        uuid_plan: requestData.uuid_plan,
        contract_number: requestData.numberContract || requestData.contract_number || null,
        month: requestData.month || null,
        year: requestData.year || null,
      };

      const response = await apiClient.post('coupons/check-quota', cleanedData);
      return response.data;
    },
    onError: (error) => {
      console.error('Error checking quota:', error);
    },
  });
};

export const useCouponsDetails = () => {
  return useMutation({
    mutationFn: async (coupons_uuids) => {
      const response = await apiClient.post('/coupons/paids', { coupons_uuids });
      return response.data;
    },
    onError: (error) => {
      console.error('Error getting coupons details:', error);
    },
  });
};

export const useRegisterPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (register_payment) => {
      const response = await apiClient.put('coupons/payment/register', register_payment);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
      queryClient.invalidateQueries({ queryKey: couponKeys.deferred() });
    },
    onError: (error) => {
      console.error('Error registering payment:', error);
    },
  });
};

export const useRegisterExternalPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('coupons/external-payment/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.externalPayments() });
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
    },
    onError: (error) => {
      console.error('Error registering external payment:', error);
    },
  });
};

export const useVerifyExternalPayment = () => {
  return useMutation({
    mutationFn: async ({ file, provider }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('provider', provider);

      const response = await apiClient.post('coupons/external-payment/verify', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error('Error verifying external payment:', error);
    },
  });
};

export const useSendExternalPaymentData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      uuid_user,
      uuid_branch,
      entidadRecaudadora,
      puntoDeVenta,
      electronicInvoice,
      fechaFactura,
      file,
      provider,
    }) => {
      const formData = new FormData();
      formData.append('user_uuid', uuid_user);
      formData.append('branch_uuid', uuid_branch);
      formData.append('file', file);
      formData.append('legal_entity_uuid', entidadRecaudadora);
      formData.append('sale_point_uuid', puntoDeVenta);
      formData.append('entry_date', fechaFactura);
      formData.append('electronic_invoice', !electronicInvoice);
      formData.append('provider', provider);

      const response = await apiClient.post('coupons/external-payment/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponKeys.externalPayments() });
      queryClient.invalidateQueries({ queryKey: couponKeys.lists() });
    },
    onError: (error) => {
      console.error('Error sending external payment data:', error);
    },
  });
};

export const useAnalyzeXnetFile = () => {
  return useMutation({
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('coupons/external-payment/analyze-xnet', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error('Error analyzing XNET file:', error);
    },
  });
};

export const useGenerateXnetFile = () => {
  return useMutation({
    mutationFn: async ({ selectedCoupons }) => {
      const response = await apiClient.post('coupons/external-payment/generate-xnet', {
        selected_coupons: selectedCoupons
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `xnet_generated_${timestamp}.txt`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Archivo XNET generado correctamente' };
    },
    onError: (error) => {
      console.error('Error generating XNET file:', error);
    },
  });
};
